import { css, Interpolation, Theme } from '@emotion/react';
import { WeaveLogo } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { TextLink } from '@frontend/design-system';
import { useFillingExperienceContext } from '../filling-experience/provider';

const contentContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing(1, 1.5)};
  gap: ${theme.spacing(1)};
`;

const weaveLogoStyles = css`
  width: 80px;
  height: auto;
  display: inline;
`;

const weaveLogoContainerStyles = css`
  display: flex;
  align-items: center;
  color: ${theme.colors.neutral90};
  gap: ${theme.spacing(1)};
`;

const textLinkStyles = css`
  color: ${theme.colors.neutral90};
`;

const linksContainerStyle = css`
  display: inline;
  text-align: center;

  @media (max-width: ${breakpoints.small.min}px) {
    font-size: ${theme.fontSize(12)};
  }
`;

interface Props {
  /** Optional locationId to use directly, bypassing the FillingExperienceContext. */
  locationId?: string;
  containerCss?: Interpolation<Theme>;
}

const UnstyledFooterContent = ({
  locationId,
  containerCss,
}: {
  locationId: string;
  containerCss?: Interpolation<Theme>;
}) => {
  const { t } = useTranslation('digital-forms-experience');

  const privacyPolicyLink = (
    <TextLink css={textLinkStyles} to={`/legal/privacy-policy/${locationId}`} target='_blank'>
      {t('Privacy Policy')}
      {' | '}
    </TextLink>
  );
  const termsAndConditionsLink = (
    <TextLink css={textLinkStyles} to={`/legal/terms-conditions/${locationId}`} target='_blank'>
      {t('Terms and Conditions')}
      {' | '}
    </TextLink>
  );
  const hipaaLink = (
    <TextLink css={textLinkStyles} target='_blank' href='https://www.weavehelp.com/hc/en-us/articles/5017862597275'>
      {t('HIPAA Compliance')}
    </TextLink>
  );

  const poweredByLink = (
    <TextLink css={weaveLogoContainerStyles} href='https://getweave.com' target='_blank'>
      {t('Powered by')}
      <WeaveLogo css={weaveLogoStyles} />
    </TextLink>
  );

  return (
    <div css={[contentContainerStyle, containerCss]}>
      <div css={linksContainerStyle}>
        {privacyPolicyLink}
        {termsAndConditionsLink}
        {hipaaLink}
      </div>
      {poweredByLink}
    </div>
  );
};

const UnstyledFooterWithContext = (props: Props) => {
  const { locationId } = useFillingExperienceContext();
  return <UnstyledFooterContent {...props} locationId={locationId} />;
};

const UnstyledFooterLinks = ({ locationId, ...rest }: Props) => {
  if (locationId !== undefined) {
    return <UnstyledFooterContent locationId={locationId} {...rest} />;
  }
  return <UnstyledFooterWithContext {...rest} />;
};
export default UnstyledFooterLinks;
